import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { enumLabel } from "@/i18n/enumLabels";
import { api, openAuthedFile, type ApiResponse } from "@/api/client";
import {
  INVOICE_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  type InvoiceType,
  type InvoiceStatus,
  type PaymentMethod,
} from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import { FileText, Plus, Download, Loader2, Receipt } from "lucide-react";

interface InvoiceRow {
  id: string;
  number: string;
  type: InvoiceType;
  status: InvoiceStatus;
  total: number;
  currency: string;
  dueDate?: string | null;
  vs?: string | null;
}

interface PaymentRow {
  id: string;
  direction: "in" | "out";
  amount: number;
  currency: string;
  method: PaymentMethod;
  paidAt: string;
  note?: string | null;
}

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  issued: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-100 text-gray-500",
};

/**
 * Invoices & payments for an order (Block 2 B2.5). Issue proforma/final invoices (PDF
 * persisted to R2), open the authed PDF, and see the payment ledger entries. Recording a
 * payment lives in StatusActions (it derives the order status via the ledger).
 */
export default function InvoicePanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  const { data: invData, isLoading } = useQuery({
    queryKey: ["order-invoices", orderId],
    queryFn: () =>
      api.get<{ data: InvoiceRow[] }>(`/api/admin/invoices?orderId=${orderId}&limit=50`),
  });
  const { data: payData } = useQuery({
    queryKey: ["order-payments", orderId],
    queryFn: () => api.get<ApiResponse<PaymentRow[]>>(`/api/admin/payments?orderId=${orderId}`),
  });

  const issue = useMutation({
    mutationFn: (type: InvoiceType) => api.post(`/api/admin/invoices`, { orderId, type }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-invoices", orderId] });
    },
  });

  const openPdf = async (inv: InvoiceRow) => {
    setPdfBusy(inv.id);
    try {
      await openAuthedFile(`/api/admin/invoices/${inv.id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  const invoices = invData?.data ?? [];
  const pays = payData?.data ?? [];

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Receipt className="h-5 w-5" /> {t("orders.invoices.title")}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => issue.mutate("proforma")}
            disabled={issue.isPending}
            className="btn-ghost btn-sm"
          >
            <Plus className="h-4 w-4" /> {t("orders.invoices.proforma")}
          </button>
          <button
            onClick={() => issue.mutate("final")}
            disabled={issue.isPending}
            className="btn-ghost btn-sm"
          >
            <Plus className="h-4 w-4" /> {t("orders.invoices.final")}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("common.states.loading")}
        </div>
      ) : invoices.length === 0 ? (
        <p className="py-3 text-sm text-gray-400">{t("orders.invoices.empty")}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {invoices.map((inv) => (
            <li key={inv.id} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="font-mono text-sm text-gray-900 dark:text-white">{inv.number}</p>
                  <p className="text-xs text-gray-500">
                    {enumLabel(INVOICE_TYPE_LABELS, inv.type)}
                    {inv.dueDate
                      ? ` · ${t("orders.invoices.due", { date: formatDate(inv.dueDate) })}`
                      : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {formatPrice(inv.total, inv.currency)}
                </span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[inv.status]}`}
                >
                  {enumLabel(INVOICE_STATUS_LABELS, inv.status)}
                </span>
                <button
                  onClick={() => openPdf(inv)}
                  className="btn-ghost btn-sm"
                  title={t("orders.invoices.openPdf")}
                  disabled={pdfBusy === inv.id}
                >
                  {pdfBusy === inv.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pays.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-700">
          <h3 className="mb-2 text-xs font-semibold uppercase text-gray-400">
            {t("orders.invoices.payments")}
          </h3>
          <ul className="space-y-1.5">
            {pays.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-300">
                  {formatDate(p.paidAt)} · {enumLabel(PAYMENT_METHOD_LABELS, p.method)}
                </span>
                <span className={p.direction === "in" ? "text-emerald-600" : "text-red-600"}>
                  {p.direction === "in" ? "+" : "−"}
                  {formatPrice(p.amount, p.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
